import { listingAPI } from '../lib/api';

export interface ExperienceTagRef {
  tag: { id: string; name: string; slug: string; group: string };
}

export interface ExperiencePricing {
  experienceId: string;
  tenantId: string;
  pricePerGuest: number;
  currency: string;
  seasonalPricing?: Array<{ id: string; name: string; startDate: string; endDate: string; priceMultiplier: number }>;
}

export interface ExperienceAvailabilitySlot {
  id: string;
  experienceId: string;
  date: string;
  slot: string;
  capacity: number;
  booked: number;
}

export interface Experience {
  id: string;
  tenantId: string;
  title: string;
  description?: string | null;
  duration: string;
  groupSizeMin: number;
  groupSizeMax?: number | null;
  addressLine1?: string | null;
  city: string;
  state?: string | null;
  country: string;
  latitude?: number | null;
  longitude?: number | null;
  status: 'DRAFT' | 'PENDING_REVIEW' | 'PUBLISHED' | 'SUSPENDED' | 'ARCHIVED';
  createdAt: string;
  updatedAt: string;
  pricing?: ExperiencePricing | null;
  tags?: ExperienceTagRef[];
  media?: Array<{ url: string }>;
  /** Present only on geo-search (lat/lng) results — see experience.service.ts's geoList. */
  distanceKm?: number;
}

export interface ExperienceFilter {
  page?: number;
  limit?: number;
  location?: string;
  city?: string;
  search?: string;
  /** Geo search — providing lat/lng switches the backend from a text/city filter to a
   * Haversine-radius search (see listing-service's experience.service.ts). */
  lat?: number;
  lng?: number;
  radius?: number;
  /** ExperienceTag slug (e.g. "water", "adventure") — see backend/.../experience.validation.ts */
  theme?: string;
  status?: Experience['status'] | 'ALL';
  sortBy?: 'createdAt' | 'title' | 'city';
  order?: 'asc' | 'desc';
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

interface PaginatedEnvelope<T> {
  success: boolean;
  data: T[];
  meta: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean };
}

/** Matches backend createExperienceSchema body (experience.validation.ts) */
export interface CreateExperienceRequest {
  title: string;
  description?: string;
  duration: string;
  groupSizeMin?: number;
  groupSizeMax?: number;
  addressLine1?: string;
  city: string;
  state?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  tagIds?: string[];
  /** Present only when a platform-tier role (SUPER_ADMIN/PLATFORM_ADMIN/PLATFORM_STAFF) is
   * creating this experience on behalf of an organization. */
  organizationId?: string;
}

/** Matches backend updateExperiencePricingSchema body */
export interface UpdateExperiencePricingRequest {
  pricePerGuest: number;
  currency?: string;
}

export const experienceService = {
  list: (filters: ExperienceFilter = {}) =>
    listingAPI.get<PaginatedEnvelope<Experience>>('/v1/experiences', { params: filters }).then((r) => r.data),

  getById: (id: string) => listingAPI.get<Envelope<Experience>>(`/v1/experiences/${id}`).then((r) => r.data),

  getAvailability: (id: string, from: string, to: string) =>
    listingAPI
      .get<Envelope<ExperienceAvailabilitySlot[]>>(`/v1/experiences/${id}/availability`, { params: { from, to } })
      .then((r) => r.data),

  getPricing: (id: string) =>
    listingAPI.get<Envelope<ExperiencePricing>>(`/v1/experiences/${id}/pricing`).then((r) => r.data),

  /** Create a new experience — returns the created experience with its ID */
  create: (data: CreateExperienceRequest) =>
    listingAPI.post<Envelope<Experience>>('/v1/experiences', data),

  /** Set/replace the per-guest pricing for an experience */
  updatePricing: (id: string, data: UpdateExperiencePricingRequest) =>
    listingAPI.patch(`/v1/experiences/${id}/pricing`, data).then((r) => r.data),

  /** Replace experience tags */
  setExperienceTags: (id: string, tagIds: string[]) =>
    listingAPI.put(`/v1/experiences/${id}/experience-tags`, { tagIds }).then((r) => r.data),

  /** PENDING_REVIEW -> PUBLISHED. Only PLATFORM_ADMIN/SUPER_ADMIN can actually complete this
   *  transition (backend/listing-service/src/config/constants.ts VALID_STATUS_TRANSITIONS --
   *  ORG_ADMIN/ORG_MANAGER cannot self-publish). */
  publish: (id: string) => listingAPI.post(`/v1/experiences/${id}/publish`).then((r) => r.data),

  /** Experiences I own or an ORG_ADMIN has delegated to me (see ExperienceAssignment). Mirrors
   *  propertyService.getMyProperties exactly. */
  getMyExperiences: (page = 1, limit = 10) =>
    listingAPI.get<PaginatedEnvelope<Experience>>('/v1/experiences/mine', { params: { page, limit } }).then((r) => r.data),

  /** Delegated experience management -- mirrors propertyService's assignment methods exactly. */
  listAssignments: (experienceId: string) =>
    listingAPI.get<{ data: Array<{ id: string; experienceId: string; userId: string; tenantId: string; createdAt: string }> }>(`/v1/experiences/${experienceId}/assignments`).then((r) => r.data.data),

  assignUser: (experienceId: string, userId: string) =>
    listingAPI.post<{ data: unknown }>(`/v1/experiences/${experienceId}/assignments`, { userId }).then((r) => r.data.data),

  unassignUser: (experienceId: string, userId: string) =>
    listingAPI.delete<{ data: unknown }>(`/v1/experiences/${experienceId}/assignments/${userId}`).then((r) => r.data.data),

  /**
   * Step 1 of the media upload flow — get a presigned S3 PUT URL.
   * Backend: POST /v1/media/presign { folder: 'experiences/images', experienceId, ... }
   */
  presignUpload: (params: { experienceId: string; originalName: string; contentType: string }) =>
    listingAPI.post<{ data: { uploadUrl: string; s3Key: string; publicUrl: string | null; expiresInSeconds: number } }>(
      '/v1/media/presign',
      { folder: 'experiences/images', ...params },
    ).then((r) => r.data.data),

  /**
   * Step 2 of the media upload flow — create the DB record after PUT to S3 succeeds.
   * Backend: POST /v1/media/confirm { s3Key, experienceId, contentType, fileSize }
   */
  confirmUpload: (params: { s3Key: string; experienceId: string; contentType: string; fileSize: number }) =>
    listingAPI.post('/v1/media/confirm', params).then((r) => r.data),

  /** Upload a photo: presign -> PUT to S3 -> confirm. Mirrors propertyService.uploadPhoto. */
  uploadPhoto: async (experienceId: string, file: File): Promise<void> => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    const MIME_MAP: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg',
      png: 'image/png', webp: 'image/webp', gif: 'image/gif',
    };
    const contentType = file.type || MIME_MAP[ext] || 'image/jpeg';

    const { uploadUrl, s3Key } = await experienceService.presignUpload({
      experienceId,
      originalName: file.name,
      contentType,
    });
    const putResp = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
    if (!putResp.ok) throw new Error(`S3 upload failed: ${putResp.status} ${putResp.statusText}`);
    await experienceService.confirmUpload({
      s3Key,
      experienceId,
      contentType,
      fileSize: file.size,
    });
  },
};
